import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarDays, Undo2, Sparkles } from "lucide-react";
import { fmtMelbDay } from "@/lib/date";
import MilestoneHistory from "@/components/milestones/MilestoneHistory";
import MilestoneDateDialog from "@/components/milestones/MilestoneDateDialog";
import MilestoneAchieveDialog from "@/components/milestones/MilestoneAchieveDialog";

const dayOr = (v, unknown) => (unknown || !v ? "date unknown" : fmtMelbDay(v));

function StatusLine({ record, kind }) {
  if (!record?.ever_achieved) return <span className="text-slate-400">Not recorded yet</span>;
  if (kind === "achievement") return <span>Achieved {dayOr(record.first_achieved_at, record.first_date_unknown)}</span>;
  return (
    <span>
      {record.current ? `Currently qualified · since ${dayOr(record.current_since)}` : `Not currently qualified · ended ${fmtMelbDay(record.last_ceased_at)}`}
      <span className="text-slate-500"> · First achieved {dayOr(record.first_achieved_at, record.first_date_unknown)}</span>
    </span>
  );
}

export default function MilestoneRow({ item, kind, record, hint, canEdit, busy, today, onAct, testid }) {
  const [confirm, setConfirm] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [achieveOpen, setAchieveOpen] = useState(false);
  const checked = !!record?.current;
  const hasActive = !!record?.last_active_event_id;

  const onChange = (v) => {
    if (v) return setAchieveOpen(true);
    if (kind === "qualification") return onAct({ action: "uncheck" });
    setConfirm("uncheck");
  };

  return (
    <div className={`rounded-xl border bg-white p-3 sm:p-4 ${checked ? "border-[#C9A961]/70" : "border-slate-200"}`} data-testid={testid}>
      <div className="flex items-start gap-3">
        <Checkbox checked={checked} disabled={!canEdit || busy} onCheckedChange={onChange} data-testid={`${testid}-checkbox`}
                  className="mt-0.5 h-5 w-5 border-[#0A1F44]/40 data-[state=checked]:bg-[#C9A961] data-[state=checked]:border-[#C9A961] data-[state=checked]:text-[#0A1F44]" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900">{item.title}</div>
          <div className="text-[11px] text-[#0A1F44]/80 mt-0.5" data-testid={`${testid}-status`}><StatusLine record={record} kind={kind} /></div>
          {hint && !checked && (
            <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] rounded-full bg-[#C9A961]/15 text-[#7A5E16] px-2 py-0.5" data-testid={`${testid}-hint`}>
              <Sparkles className="w-3 h-3" /> Ready to record: {hint}
            </div>
          )}
          {canEdit && hasActive && (
            <div className="flex flex-wrap gap-3 mt-2">
              <button onClick={() => setDateOpen(true)} data-testid={`${testid}-set-date`}
                      className="inline-flex items-center gap-1 text-[11px] text-[#0A1F44] hover:text-[#8A6D1F]">
                <CalendarDays className="w-3.5 h-3.5" /> {record?.first_date_unknown && !record?.current_since ? "Add the date" : "Correct the date"}
              </button>
              {kind === "qualification" && (
                <button onClick={() => setConfirm("correct")} data-testid={`${testid}-correct`}
                        className="inline-flex items-center gap-1 text-[11px] text-[#8A1F1F] hover:underline">
                  <Undo2 className="w-3.5 h-3.5" /> Correct a mistaken entry
                </button>
              )}
            </div>
          )}
          <MilestoneHistory record={record} kind={kind} testid={testid} />
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Correct this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "uncheck"
                ? "This removes an accidental entry. The original entry and this correction stay in the history."
                : "This undoes the most recent change as a mistake. It is recorded as a correction, not as losing the qualification."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testid}-confirm-correct`} className="bg-[#0A1F44]"
                               onClick={() => { onAct({ action: confirm }); setConfirm(null); }}>Record correction</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MilestoneAchieveDialog open={achieveOpen} onOpenChange={setAchieveOpen} title={item.title} today={today} first={!record?.ever_achieved}
                              onSave={(b) => { onAct({ action: "check", ...b }); setAchieveOpen(false); }} />
      <MilestoneDateDialog open={dateOpen} onOpenChange={setDateOpen} title={item.title} today={today}
                           onSave={(d, note) => { onAct({ action: "set_date", achieved_on: d, note }); setDateOpen(false); }} />
    </div>
  );
}
